import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, type ChartConfig } from '../ui/chart';
import { Bar, BarChart, Pie, PieChart, Cell, XAxis, YAxis, CartesianGrid, RadialBarChart, RadialBar, PolarGrid, PolarRadiusAxis, Label } from 'recharts';

const COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

const footfallChartConfig: ChartConfig = {
  value: {
    label: 'Footfall',
    color: 'hsl(var(--brand-primary))',
  },
};

const interactionsChartConfig: ChartConfig = {
  value: {
    label: 'Interactions',
    color: 'hsl(var(--brand-primary))',
  },
};

const genderChartConfig: ChartConfig = {
  female: {
    label: 'Female',
    color: '#f8b4cb', // Light pink
  },
  male: {
    label: 'Male',
    color: 'hsl(var(--brand-primary))',
  },
};

const ageChartConfig: ChartConfig = {
  'Under 16': { label: 'Under 16', color: COLORS[0] },
  '16-24': { label: '16-24', color: COLORS[1] },
  '25-44': { label: '25-44', color: COLORS[2] },
  '45-64': { label: '45-64', color: COLORS[3] },
  '65+': { label: '65+', color: COLORS[4] },
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number | string | null | undefined;
  format?: 'number' | 'currency' | 'percentage' | 'time';
  description?: string;
  highlighted?: boolean;
  secondaryLine?: string;
  locationBreakdown?: Array<{ name: string; machineCount: number }>;
}

export function StatCard({ title, value, format = 'number', description, highlighted = false, secondaryLine, locationBreakdown }: StatCardProps) {
  const [displayValue, setDisplayValue] = useState<number | string>(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const hasBreakdown = locationBreakdown && locationBreakdown.length > 0;

  const formatValue = (val: number | string | null | undefined): string => {
    if (val === null || val === undefined) {
      return '0';
    }
    if (typeof val === 'string') return val;
    
    switch (format) {
      case 'currency':
        return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'percentage':
        return `${val.toFixed(1)}%`;
      case 'time':
        return `${val} mins`;
      case 'number':
      default:
        return val.toLocaleString('en-GB');
    }
  };

  useEffect(() => {
    if (value === null || value === undefined) {
      setDisplayValue(0);
      return;
    }

    if (!highlighted || typeof value === 'string') {
      setDisplayValue(value);
      return;
    }

    setIsAnimating(true);
    const targetValue = value;
    const duration = 1500; // 1.5 seconds
    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + (targetValue - startValue) * easeOut);
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(targetValue);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [value, highlighted]);

  return (
    <Card 
      className={highlighted ? 'min-h-[160px] flex flex-col relative' : ''}
      style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}
    >
      <CardContent className={highlighted ? 'pt-4 pb-4 flex flex-col flex-1 justify-center' : 'pt-3'}>
        {!hasBreakdown && (
          <p className={highlighted ? 'text-xs font-semibold text-brand-navy mb-3 text-center' : 'text-xs font-medium text-brand-navy mb-1'}>{title}</p>
        )}
        {!hasBreakdown && (
          <div className={highlighted ? 'text-4xl font-extrabold text-brand-primary text-center mb-2' : 'text-2xl font-extrabold text-brand-primary'} style={{ color: '#183df7' }}>{formatValue(displayValue)}</div>
        )}
        {secondaryLine && !hasBreakdown && (
          <p className="text-xs text-brand-navy text-center mt-1">{secondaryLine}</p>
        )}
        {description && !secondaryLine && !hasBreakdown && <p className="text-xs text-brand-navy mt-1 text-center">{description}</p>}
        {hasBreakdown && (
          <div className="mt-2">
            <div className="space-y-1.5">
              {locationBreakdown.map((location, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm py-1">
                  <span className="text-brand-navy font-medium">{location.name}</span>
                  <span className="text-brand-primary font-semibold" style={{ color: '#183df7' }}>{location.machineCount} {location.machineCount === 1 ? 'machine' : 'machines'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Progress Bar Component (Radial Chart)
interface ProgressBarProps {
  title: string;
  value: number;
  max?: number;
  format?: 'percentage' | 'number';
}

const progressBarChartConfig = {
  value: {
    label: 'Value',
    color: 'hsl(var(--brand-primary))',
  },
} satisfies ChartConfig;

export function ProgressBar({ title, value, max = 100, format = 'percentage' }: ProgressBarProps) {
  const percentage = (value / max) * 100;
  const displayValue = format === 'percentage' ? `${value}%` : value.toLocaleString('en-GB');
  
  const chartData = [
    { 
      value: Math.min(percentage, 100), 
      fill: 'hsl(var(--brand-primary))',
      max: 100 
    },
  ];

  return (
    <Card className="flex flex-col" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
      <CardHeader className="items-center pb-0">
        <CardTitle className="text-brand-primary text-sm" style={{ color: '#183df7' }}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        <ChartContainer
          config={progressBarChartConfig}
          className="mx-auto aspect-square max-h-[250px]"
        >
          <RadialBarChart
            data={chartData}
            startAngle={0}
            endAngle={250}
            innerRadius={80}
            outerRadius={110}
          >
            <PolarGrid
              gridType="circle"
              radialLines={false}
              stroke="none"
              className="first:fill-muted last:fill-background"
              polarRadius={[86, 74]}
            />
            <RadialBar dataKey="value" background cornerRadius={10} />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className="fill-brand-primary text-4xl font-bold"
                          style={{ fill: '#183df7' }}
                        >
                          {displayValue}
                        </tspan>
                      </text>
                    )
                  }
                }}
              />
            </PolarRadiusAxis>
          </RadialBarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// Demographics Pie Chart (Gender Split)
interface DemographicsPieChartProps {
  female: number;
  male: number;
}

export function DemographicsPieChart({ female, male }: DemographicsPieChartProps) {
  const brandColors = {
    female: '#f8b4cb', // Light pink
    male: 'hsl(var(--brand-primary))',
  };

  const total = female + male;
  const data = [
    { name: 'Female', value: female, fill: brandColors.female, percent: total > 0 ? ((female / total) * 100).toFixed(0) : '0' },
    { name: 'Male', value: male, fill: brandColors.male, percent: total > 0 ? ((male / total) * 100).toFixed(0) : '0' },
  ];

  return (
    <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-brand-primary text-sm" style={{ color: '#183df7' }}>Gender Demographics</CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        <ChartContainer config={genderChartConfig} className="h-[180px]">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={false}
              outerRadius={70}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <ChartTooltip 
              content={<ChartTooltipContent 
                formatter={(value: any, name: any) => {
                  const numValue = typeof value === 'number' ? value : parseFloat(value);
                  const percentage = total > 0 ? ((numValue / total) * 100).toFixed(0) : '0';
                  return `${name}: ${percentage}%`;
                }}
              />}
            />
            <ChartLegend 
              content={({ payload }) => (
                <div className="flex items-center justify-center gap-4 mt-4">
                  {payload?.map((entry: any, index: number) => {
                    const dataEntry = data[index];
                    return (
                      <div key={`legend-${index}`} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm" 
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-xs text-brand-navy">
                          {dataEntry.name}: {dataEntry.percent}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// Age Demographics Pie Chart
interface AgeDemographicsPieChartProps {
  data: Record<string, number>;
}

// Migration function to map old age ranges to new ones
function migrateAgeRanges(data: Record<string, number>): Record<string, number> {
  const migrated: Record<string, number> = {};

  // Map old keys to new keys
  const oldToNewMap: Record<string, string> = {
    '18-24': '16-24',
    '25-34': '25-44',
    '35-44': '25-44',
    '45-54': '45-64',
    '55+': '65+',
  };

  Object.entries(data).forEach(([key, value]) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (numValue !== undefined && numValue !== null && !isNaN(numValue) && numValue > 0) {
      // If it's an old key, map it to new key
      const newKey = oldToNewMap[key] || key;
      // Combine values if multiple old keys map to the same new key
      migrated[newKey] = (migrated[newKey] || 0) + numValue;
    }
  });

  return migrated;
}

export function AgeDemographicsPieChart({ data }: AgeDemographicsPieChartProps) {
  // Migrate old age ranges to new ones
  const migratedData = migrateAgeRanges(data);

  // Filter out empty/zero values and convert to numbers
  const entries = Object.entries(migratedData)
    .filter(([_, value]) => {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return numValue !== undefined && numValue !== null && !isNaN(numValue) && numValue > 0;
    })
    .map(([age, value]) => {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return { age, value: numValue || 0 };
    });

  const total = entries.reduce((sum, item) => sum + item.value, 0);

  if (entries.length === 0 || total === 0) {
    return (
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-brand-primary text-sm" style={{ color: '#183df7' }}>Age Demographics</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <p className="text-sm text-muted-foreground text-center py-8">No age demographic data available</p>
        </CardContent>
      </Card>
    );
  }

  // Build data array similar to Gender chart, using colors from config
  const chartData = entries.map(({ age, value }) => {
    const configColor = ageChartConfig[age as keyof typeof ageChartConfig]?.color || COLORS[0];
    return {
      name: age,
      value,
      fill: configColor,
      percent: total > 0 ? ((value / total) * 100).toFixed(0) : '0',
    };
  });

  return (
    <Card className="overflow-hidden" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-brand-primary text-sm" style={{ color: '#183df7' }}>Age Demographics</CardTitle>
      </CardHeader>
      <CardContent className="pt-2 pb-4 overflow-hidden">
        <ChartContainer config={ageChartConfig} className="h-[240px]">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="45%"
              labelLine={false}
              label={false}
              outerRadius={60}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <ChartTooltip 
              content={<ChartTooltipContent 
                formatter={(value: any, name: any) => {
                  const numValue = typeof value === 'number' ? value : parseFloat(value);
                  const percentage = total > 0 ? ((numValue / total) * 100).toFixed(0) : '0';
                  return `${name}: ${percentage}%`;
                }}
              />}
            />
            <ChartLegend 
              content={({ payload }) => (
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mt-6 px-2 max-w-full">
                  {payload?.map((entry: any, index: number) => {
                    const dataEntry = chartData[index];
                    return (
                      <div key={`legend-${index}`} className="flex items-center gap-1.5 min-w-0 flex-shrink">
                        <div 
                          className="w-3 h-3 rounded-sm flex-shrink-0" 
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-sm text-brand-navy whitespace-nowrap truncate">
                          {entry.value}: {dataEntry.percent}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// Footfall Bar Chart (Weekly breakdown by day)
interface FootfallBarChartProps {
  data: Record<string, number>;
  total?: number;
}

export function FootfallBarChart({ data, total }: FootfallBarChartProps) {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  const chartData = days.map((day, index) => ({
    day: dayLabels[index],
    value: data[day] || 0,
  }));

  const maxValue = Math.max(...chartData.map(d => d.value), 0);

  return (
    <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-baseline">
          <CardTitle className="text-brand-primary text-sm" style={{ color: '#183df7' }}>Weekly Footfall</CardTitle>
          {total && (
            <span className="text-base font-bold text-brand-primary" style={{ color: '#183df7' }}>{total.toLocaleString('en-GB')}</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <ChartContainer config={footfallChartConfig} className="h-[280px]">
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
            <XAxis 
              dataKey="day" 
              tick={{ fontSize: 11, fill: 'hsl(var(--brand-navy))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'hsl(var(--brand-navy))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickFormatter={(value) => value.toLocaleString('en-GB')}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar 
              dataKey="value" 
              fill="hsl(var(--brand-primary))"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// Time Series Chart (for Machine Interactions)
interface TimeSeriesChartProps {
  title: string;
  daily?: number;
  weekly?: number;
  monthly?: number;
}

export function TimeSeriesChart({ title, daily, weekly, monthly }: TimeSeriesChartProps) {
  const chartData = [];
  
  if (daily !== undefined) {
    chartData.push({ period: 'Daily', value: daily });
  }
  if (weekly !== undefined) {
    chartData.push({ period: 'Weekly', value: weekly });
  }
  if (monthly !== undefined) {
    chartData.push({ period: 'Monthly', value: monthly });
  }

  const maxValue = Math.max(...chartData.map(d => d.value), 0);

  return (
    <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-brand-primary text-sm" style={{ color: '#183df7' }}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        {chartData.length > 0 ? (
          <ChartContainer config={interactionsChartConfig} className="h-[280px]">
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 11, fill: 'hsl(var(--brand-navy))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: 'hsl(var(--brand-navy))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickFormatter={(value) => value.toLocaleString('en-GB')}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar 
                dataKey="value" 
                fill="hsl(var(--brand-primary))"
                radius={[4, 4, 0, 0]}
                barSize={60}
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <p className="text-sm text-brand-navy">No data available</p>
        )}
      </CardContent>
    </Card>
  );
}
